/**
 * Client-side report exports that reproduce the Report Studio preview:
 * ordered columns, per-column filters, grouping, totals row, and styling.
 *
 * The server /reports/generate path renders the fixed Debt Pipeline
 * workbook/PDF with the deployment client's mark (from the client profile)
 * template when no viewGrid is supplied. When the Pipeline toolbar or Report
 * Studio sends filters.viewGrid (headers + display cells from the on-screen
 * columns), PDF/Excel match that matrix instead.
 *
 * Studio-exact Excel with grouping/totals/styling is still produced here via
 * exportStyledXlsx from the same rows the preview renders.
 *
 * Spreadsheet-specific rules that differ from the on-screen preview:
 *  - numbers are written as real numbers with a number format, never as the
 *    pre-formatted display string, so Excel can sort, sum, and chart them;
 *  - a missing value is an empty cell, not an em dash, because "—" turns a
 *    numeric column into text and silently breaks AVERAGE/SUM;
 *  - exports default to the light print palette even when the preview is dark,
 *    since a workbook is a print-and-share artifact.
 */
import type { Workbook } from 'exceljs'
import { clientProfileService } from '@/services/clientProfile'
import { safeBrandingPath } from '@/utils/brandingPath'
import type { Loan } from '@/types'
import { COLUMN_MAP } from './columns'
import type { ColumnDef } from './columnTypes'
import { DERIVED_LEGEND, anyDerived, derivedExportBasis, exportCell } from './derivedMark'
import { pctNumFmt } from './helpers'
import { aggregate, formatAggValue, groupLoans } from './reporting'
import type { AggMethod, ColumnId, ReportConfig, ReportStyle } from './types'
import { PAPER_SIZES, REPORT_STYLE_PRESETS } from './types'

const MISSING = '—'
/** Default printed title when the studio title field / view name is empty. */
export const DEFAULT_REPORT_TITLE = 'Pipeline Report'

type ClientLogo = { base64: string; width: number; height: number }

/** Read width/height from a PNG IHDR so Excel can keep the mark's aspect ratio. */
function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 24) return null
  // PNG signature + IHDR chunk: width/height are big-endian at bytes 16–23.
  const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19]
  const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23]
  if (width <= 0 || height <= 0) return null
  return { width, height }
}

/**
 * The mark a workbook carries: the client profile's mark for light backgrounds
 * (a workbook is a print-and-share sheet, white under its title row). It is the
 * deployment's own client, read from settings — never a file named in code.
 * No profile, or none recorded, is no mark: the title row degrades to type.
 */
async function clientLogoUrl(): Promise<string> {
  try {
    const profile = await clientProfileService.get()
    return profile.logoDark || profile.logoMark || ''
  } catch {
    return ''
  }
}

async function loadClientLogo(logoUrl?: string): Promise<ClientLogo | null> {
  try {
    // Only a same-origin /branding file is ever fetched, whoever supplied the URL.
    const url = safeBrandingPath(logoUrl ?? (await clientLogoUrl()))
    if (!url) return null
    const res = await fetch(url)
    if (!res.ok) return null
    const bytes = new Uint8Array(await res.arrayBuffer())
    const size = pngSize(bytes)
    if (!size) return null
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return { base64: btoa(binary), width: size.width, height: size.height }
  } catch {
    return null
  }
}

/** Approximate Excel column width in pixels (Calibri 11 ≈ 7px per character unit). */
function excelColWidthPx(charWidth: number | undefined): number {
  return Math.max(8, (charWidth ?? 10) * 7)
}

/**
 * Place the client mark so its right edge lands on the right edge of `lastCol`
 * (1-based), scaled to `targetHeightPx` without distorting aspect ratio.
 */
function logoPlacement(
  sheet: { getColumn: (n: number) => { width?: number } },
  lastCol: number,
  nativeW: number,
  nativeH: number,
  targetHeightPx = 28,
): { startCol: number; width: number; height: number } {
  const height = targetHeightPx
  const width = Math.max(1, Math.round((nativeW / nativeH) * height))
  let remaining = width
  let col = lastCol
  while (col >= 1 && remaining > 0) {
    const colPx = excelColWidthPx(sheet.getColumn(col).width)
    if (remaining <= colPx) {
      // 0-based fractional column: inset from the left of this column.
      const inset = (colPx - remaining) / colPx
      return { startCol: col - 1 + inset, width, height }
    }
    remaining -= colPx
    col -= 1
  }
  return { startCol: 0, width, height }
}

/** Display value for an export cell — em dashes become blanks. */
function exportText(raw: string): string {
  return raw === MISSING ? '' : raw
}

function csvEscape(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Palette an export renders with. 'print' keeps the operator's structural
 * choices (wrap, banding, exception flags) but forces a legible light palette.
 */
export function exportStyle(config: ReportConfig): ReportStyle {
  if (config.exportTheme === 'match') return config.style
  return {
    ...REPORT_STYLE_PRESETS['Print light'],
    wrapText: config.style.wrapText,
    zebra: config.style.zebra,
    highlightExceptions: config.style.highlightExceptions,
  }
}

function totalsCells(
  loans: Loan[],
  columns: ColumnId[],
  config: ReportConfig,
  label: string,
): string[] {
  return columns.map((columnId, index) => {
    const method: AggMethod = config.totals[columnId] ?? 'none'
    if (method === 'none') return index === 0 ? label : ''
    const value = aggregate(loans, columnId, method)
    const text = exportText(formatAggValue(value, columnId, method))
    // Keep the row self-describing even when column one carries an aggregate.
    return index === 0 ? `${label} · ${text}`.trim() : text
  })
}

export function buildCsv(loans: Loan[], columns: ColumnId[], config: ReportConfig): string {
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const lines: string[] = []
  if (config.title.trim()) lines.push(csvEscape(config.title.trim()))
  if (config.subtitle.trim()) lines.push(csvEscape(config.subtitle.trim()))
  lines.push(defs.map((def) => csvEscape(def.label)).join(','))
  for (const group of groupLoans(loans, config.groupBy)) {
    if (config.groupBy && group.key) {
      lines.push(csvEscape(`${COLUMN_MAP.get(config.groupBy)?.label ?? ''}: ${group.key}`))
    }
    for (const loan of group.rows) {
      // A derived ratio carries its mark into the file (UI-02).
      lines.push(defs.map((def) => csvEscape(exportCell(def, loan))).join(','))
    }
    if (config.groupBy && config.showTotals) {
      lines.push(totalsCells(group.rows, columns, config, 'Subtotal').map(csvEscape).join(','))
    }
  }
  if (config.showTotals) {
    lines.push(totalsCells(loans, columns, config, 'Total').map(csvEscape).join(','))
  }
  if (anyDerived(defs, loans)) lines.push(csvEscape(DERIVED_LEGEND))
  return lines.join('\r\n')
}

export function exportCsv(loans: Loan[], columns: ColumnId[], config: ReportConfig, filenameBase: string): void {
  const csv = buildCsv(loans, columns, config)
  // Leading U+FEFF BOM so Excel opens the CSV as UTF-8.
  download(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }), `${filenameBase}.csv`)
}

function argb(hex: string): string {
  const clean = hex.replace('#', '').padEnd(6, '0').slice(0, 6)
  return `FF${clean.toUpperCase()}`
}

const NUM_FMT: Record<string, string> = {
  money: '$#,##0',
  // A 0..1 ratio needs Excel's real percent format, which scales by 100. A
  // quoted "%" is a literal: 0.6766 read "0.7%" beside the grid's 67.7%.
  pct: '0.0%',
  points: '0.0"%"',
  x: '0.00"x"',
  days: '0"d"',
  int: '0',
  // The header identifies months; keep the numeric count without a suffix.
  months: '0',
}

/**
 * What Excel holds for a numeric column's cell: the number and its format, or
 * null for an empty cell. The server's view grid writes a schedule's workbook
 * the same way (backend/app/reporting/view_grid.py ``grid_numbers``), held to
 * it by the parity fixture.
 */
export function excelNumber(def: ColumnDef, loan: Loan): { value: number; numFmt?: string } | null {
  if (def.kind !== 'number') return null
  if (def.excel) return def.excel(loan)
  const numeric = def.num?.(loan) ?? null
  if (numeric == null) return null
  // A ratio stated to the hundredth shows it in Excel as on screen (`pct`).
  if (def.unit === 'pct') return { value: numeric, numFmt: pctNumFmt(numeric) }
  return { value: numeric, numFmt: def.unit ? NUM_FMT[def.unit] : undefined }
}

/** Character width Excel needs to show `text` without truncating. */
function charWidth(text: string): number {
  return text.length
}

export async function buildReportWorkbook(
  loans: Loan[],
  columns: ColumnId[],
  config: ReportConfig,
  reportName: string,
  /** The client mark to print; omitted, it is read from the client profile. */
  options: { logoUrl?: string } = {},
): Promise<Workbook> {
  const ExcelJS = (await import('exceljs')).default
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const workbook = new ExcelJS.Workbook()
  const sheetName = (config.title.trim() || reportName || DEFAULT_REPORT_TITLE)
    .replace(/[\\/*?:[\]]/g, ' ')
    .slice(0, 31)
    .trim()
  const sheet = workbook.addWorksheet(sheetName || 'Report')

  const style = exportStyle(config)
  const lastCol = Math.max(defs.length, 1)

  // Widths are declared up front: ExcelJS serializes column metadata from the
  // `columns` collection, and defining it before any cell is written is the
  // form that reliably survives round-tripping.
  sheet.columns = defs.map((def) => {
    const widest = Math.max(
      charWidth(def.label),
      ...loans.map((loan) => charWidth(exportText(def.display(loan)))),
      6,
    )
    return { width: Math.min(Math.max(widest + 3, 11), style.wrapText ? 34 : 46) }
  })

  let rowIndex = 1
  const titleText = config.title.trim() || reportName.trim() || DEFAULT_REPORT_TITLE
  const subtitleText = config.subtitle.trim()

  // Branding row: report title on the left, the client's mark flush to the last column.
  {
    const logo = await loadClientLogo(options.logoUrl)
    const titleRow = sheet.getRow(rowIndex)
    titleRow.height = logo ? Math.max(32, Math.round((logo.height / logo.width) * 110) + 8) : 32
    const titleCell = titleRow.getCell(1)
    titleCell.value = titleText
    titleCell.font = { bold: true, size: 16, color: { argb: 'FF1A2D5D' } }
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' }

    if (logo) {
      const place = logoPlacement(sheet, lastCol, logo.width, logo.height, 28)
      // Keep the title out of the mark's columns (ceil of the fractional start).
      const titleEndCol = Math.max(1, Math.floor(place.startCol))
      if (titleEndCol > 1) sheet.mergeCells(rowIndex, 1, rowIndex, titleEndCol)

      const imageId = workbook.addImage({
        base64: logo.base64,
        extension: 'png',
      })
      sheet.addImage(imageId, {
        tl: { col: place.startCol, row: 0.12 },
        ext: { width: place.width, height: place.height },
        editAs: 'oneCell',
      })
    } else if (lastCol > 1) {
      sheet.mergeCells(rowIndex, 1, rowIndex, lastCol)
    }
    rowIndex += 1
  }

  if (subtitleText) {
    const row = sheet.getRow(rowIndex)
    const cell = row.getCell(1)
    cell.value = subtitleText
    cell.font = { size: 10, color: { argb: 'FF6B7280' } }
    cell.alignment = { horizontal: 'left', vertical: 'middle' }
    if (lastCol > 1) sheet.mergeCells(rowIndex, 1, rowIndex, lastCol)
    rowIndex += 1
  }

  rowIndex += 1 // blank spacer under the brand block

  const headerRowIndex = rowIndex
  const headerRow = sheet.getRow(headerRowIndex)
  headerRow.height = style.wrapText ? 30 : 20
  defs.forEach((def, index) => {
    const cell = headerRow.getCell(index + 1)
    cell.value = def.label
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(style.headerBg) } }
    cell.font = { bold: true, color: { argb: argb(style.headerText) }, size: 10 }
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: style.wrapText,
    }
    cell.border = { bottom: { style: 'thin', color: { argb: argb(style.headerText) } } }
  })
  rowIndex += 1
  const firstDataRow = rowIndex

  let derivedCells = 0
  const writeDataRow = (loan: Loan, zebraIndex: number) => {
    const row = sheet.getRow(rowIndex)
    const banded = style.zebra && zebraIndex % 2 === 1
    defs.forEach((def, index) => {
      const cell = row.getCell(index + 1)
      const basis = derivedExportBasis(def, loan)
      if (def.kind === 'number') {
        // A missing number stays an empty cell so Excel aggregates ignore it
        // rather than coercing an em dash into text.
        const own = excelNumber(def, loan)
        if (own) {
          cell.value = own.value
          if (own.numFmt) cell.numFmt = own.numFmt
          // The number stays a number Excel can sum; the Derived mark is the
          // cell's note, and the legend row below says what it means (UI-02).
          if (basis) {
            cell.note = basis
            derivedCells += 1
          }
        }
      } else {
        const text = exportCell(def, loan)
        if (text) cell.value = text
        if (basis && text) derivedCells += 1
      }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: argb(banded ? style.rowAltBg : style.rowBg) },
      }
      cell.font = { color: { argb: argb(style.rowText) }, size: 9 }
      cell.alignment = {
        horizontal: 'center',
        vertical: 'top',
        wrapText: style.wrapText,
      }
    })
    rowIndex += 1
  }

  const writeTotalsRow = (rows: Loan[], label: string, bold: boolean) => {
    const row = sheet.getRow(rowIndex)
    defs.forEach((def, index) => {
      const cell = row.getCell(index + 1)
      const method: AggMethod = config.totals[def.id] ?? 'none'
      if (method === 'none') {
        if (index === 0) cell.value = label
      } else {
        const value = aggregate(rows, def.id, method)
        const numericAgg =
          value != null && method !== 'count' && method !== 'distinct' && def.unit && NUM_FMT[def.unit]
        if (index === 0) {
          cell.value = `${label} · ${exportText(formatAggValue(value, def.id, method))}`.trim()
        } else if (numericAgg) {
          cell.value = value
          cell.numFmt = def.unit === 'pct' ? pctNumFmt(value) : NUM_FMT[def.unit as string]
        } else if (value != null) {
          cell.value = Number.isFinite(value) && (method === 'count' || method === 'distinct')
            ? value
            : exportText(formatAggValue(value, def.id, method))
        }
      }
      cell.font = { bold, color: { argb: argb(style.headerText) }, size: 9 }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(style.headerBg) } }
      cell.alignment = {
        horizontal: 'center',
      }
      cell.border = { top: { style: 'thin', color: { argb: argb(style.headerText) } } }
    })
    rowIndex += 1
  }

  for (const group of groupLoans(loans, config.groupBy)) {
    if (config.groupBy && group.key) {
      const row = sheet.getRow(rowIndex)
      const cell = row.getCell(1)
      cell.value = `${COLUMN_MAP.get(config.groupBy)?.label ?? ''}: ${group.key}`
      cell.font = { bold: true, size: 10, color: { argb: argb(style.rowText) } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(style.rowAltBg) } }
      if (lastCol > 1) sheet.mergeCells(rowIndex, 1, rowIndex, lastCol)
      rowIndex += 1
    }
    group.rows.forEach((loan, index) => writeDataRow(loan, index))
    if (config.groupBy && config.showTotals) writeTotalsRow(group.rows, 'Subtotal', false)
  }
  const lastDataRow = rowIndex - 1
  if (config.showTotals) writeTotalsRow(loans, 'Total', true)
  if (derivedCells > 0) {
    // One legend, under the table: what the note on a derived cell means.
    const legend = sheet.getRow(rowIndex)
    const cell = legend.getCell(1)
    cell.value = DERIVED_LEGEND
    cell.font = { italic: true, size: 8, color: { argb: 'FF6B7280' } }
    cell.alignment = { horizontal: 'left', vertical: 'middle' }
    if (lastCol > 1) sheet.mergeCells(rowIndex, 1, rowIndex, lastCol)
    rowIndex += 1
  }

  sheet.views = [{ state: 'frozen', ySplit: headerRowIndex }]
  // Prints on the paper the design chose, one page wide with the header row repeated —
  // the page setup the server's workbook carries (builder._build_view_grid_xlsx).
  sheet.pageSetup = {
    paperSize: PAPER_SIZES.find((paper) => paper.id === config.paperSize)?.excelCode ?? 5,
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printTitlesRow: `${headerRowIndex}:${headerRowIndex}`,
  }
  // Filter only over the real data block, so totals never sort into the rows.
  // Grouped output interleaves banner rows, which autofilter cannot express.
  if (!config.groupBy && lastDataRow >= firstDataRow) {
    sheet.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: lastDataRow, column: lastCol },
    }
  }

  return workbook
}

export async function exportStyledXlsx(
  loans: Loan[],
  columns: ColumnId[],
  config: ReportConfig,
  filenameBase: string,
  reportName: string,
): Promise<void> {
  const workbook = await buildReportWorkbook(loans, columns, config, reportName)
  const buffer = await workbook.xlsx.writeBuffer()
  download(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${filenameBase}.xlsx`,
  )
}

/**
 * Headers + display cells for the on-screen DealGrid, in visible column order.
 * Sent as `filters.viewGrid` so PDF matches the grid; Print and Excel consume
 * the same matrix from Pipeline (buildViewGrid → getViewGrid / exportXlsxLocal).
 */
export function buildViewGrid(
  loans: Loan[],
  columns: ColumnId[],
  title?: string,
): { headers: string[]; rows: string[][]; title?: string } {
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  return {
    headers: defs.map((def) => def.label),
    // A derived ratio carries its mark into the printed grid (UI-02); the
    // server marks its own view grid the same way (view_grid.DERIVED_MARK).
    rows: loans.map((loan) => defs.map((def) => exportCell(def, loan))),
    ...(title?.trim() ? { title: title.trim() } : {}),
  }
}
